import type { MfgRecipeKind } from "@/lib/manufacturing/types";
import type {
  DictatedDraftLine,
  DictatedRecipeDraft,
} from "@/lib/manufacturing/types/recipeDictation";
import type { DuplicateRecipeDraft, DuplicateRecipeLineDraft } from "./duplicateRecipeDraft";
import { parseRecipeTagsInput } from "./recipeTagsInput";

export function isDuplicateIdentityUnchanged(
  draft: DuplicateRecipeDraft,
  code: string,
  name: string,
): boolean {
  const c = code.trim();
  const n = name.trim();
  if (!c || !n) return true;
  return (
    c.localeCompare(draft.sourceCode.trim(), undefined, { sensitivity: "accent" }) === 0 &&
    n.localeCompare(draft.sourceName.trim(), undefined, { sensitivity: "accent" }) === 0
  );
}

export function duplicateRefineTranscript(draft: DuplicateRecipeDraft): string {
  const componentLines = draft.lines.filter((ln) => ln.line_kind === "component");
  const useLines = draft.lines.filter((ln) => ln.line_kind === "use");
  const preservedNoteParts: string[] = [];
  if (componentLines.length > 0) {
    preservedNoteParts.push(
      `${componentLines.length} sub-recipe component line(s)`,
    );
  }
  if (useLines.length > 0) {
    preservedNoteParts.push(`${useLines.length} part-use line(s)`);
  }
  const preservedNote =
    preservedNoteParts.length > 0
      ? ` Includes ${preservedNoteParts.join(" and ")} that should stay unless the change explicitly replaces them.`
      : "";
  return (
    `Duplicate of source model ${draft.sourceCode} (${draft.sourceName}).` +
    ` The draft copies ${draft.lines.length} BOM line(s).${preservedNote}` +
    " Apply only the differences the user describes; leave everything else unchanged."
  );
}

function duplicateLineToDictatedLine(ln: DuplicateRecipeLineDraft): DictatedDraftLine {
  const label = ln.material_name ?? ln.material_code ?? "unknown material";
  return {
    material_name_heard: label,
    quantity: ln.quantity,
    role: ln.role,
    slot_label: ln.slot_label,
    upgrade_price_delta: ln.upgrade_price_delta,
    match: ln.material_id.trim()
      ? {
          kind: "matched",
          material_id: ln.material_id,
          canonical_name: ln.material_name ?? ln.material_code ?? label,
        }
      : { kind: "pending", reason: "no_match" },
  };
}

export function duplicateFormToDictatedDraft(
  duplicate: DuplicateRecipeDraft,
  code: string,
  name: string,
  kind: MfgRecipeKind,
  tagsInput: string,
): DictatedRecipeDraft {
  const tags = parseRecipeTagsInput(tagsInput);
  const materialLines = duplicate.lines
    .filter((ln) => ln.line_kind === "material")
    .map(duplicateLineToDictatedLine);

  return {
    code: code.trim(),
    name: name.trim(),
    kind,
    detectedKind: {
      value: kind,
      confidence: "high",
      reasoning: `Copied from ${duplicate.sourceCode}.`,
    },
    tags,
    lines: materialLines,
  };
}

function dictatedLineToDuplicateLine(ln: DictatedDraftLine): DuplicateRecipeLineDraft {
  const materialId = ln.match.kind === "matched" ? ln.match.material_id : "";
  const materialName =
    ln.match.kind === "matched" ? ln.match.canonical_name : ln.material_name_heard;
  return {
    line_kind: "material",
    material_id: materialId,
    component_recipe_id: "",
    material_use_id: "",
    quantity: ln.quantity,
    role: ln.role,
    slot_label: ln.slot_label,
    upgrade_price_delta: ln.upgrade_price_delta,
    swappable: ln.role !== "structural",
    material_code: ln.match.kind === "matched" ? undefined : ln.material_name_heard,
    material_name: materialName,
  };
}

/** Merge refined material lines back into the duplicate draft, preserving
 *  component + part-use lines in their original positions. */
export function applyDictatedDraftToDuplicate(
  duplicate: DuplicateRecipeDraft,
  dictated: DictatedRecipeDraft,
): DuplicateRecipeDraft {
  const refinedMaterials = dictated.lines.map(dictatedLineToDuplicateLine);
  let materialIdx = 0;
  const lines = duplicate.lines.map((ln) => {
    if (ln.line_kind === "component" || ln.line_kind === "use") return ln;
    const next = refinedMaterials[materialIdx];
    materialIdx += 1;
    return next ?? ln;
  });
  while (materialIdx < refinedMaterials.length) {
    lines.push(refinedMaterials[materialIdx]!);
    materialIdx += 1;
  }
  return {
    ...duplicate,
    kind: dictated.kind,
    tags: [...dictated.tags],
    lines,
  };
}

export type DuplicateFormPatch = {
  code: string;
  name: string;
  kind: MfgRecipeKind;
  tagsInput: string;
  duplicate: DuplicateRecipeDraft;
};

export function duplicateFormPatchFromDictated(
  duplicate: DuplicateRecipeDraft,
  dictated: DictatedRecipeDraft,
): DuplicateFormPatch {
  const nextDuplicate = applyDictatedDraftToDuplicate(duplicate, dictated);
  return {
    code: dictated.code,
    name: dictated.name,
    kind: dictated.kind,
    tagsInput: dictated.tags.join(", "),
    duplicate: nextDuplicate,
  };
}
